import type React from "react";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useGenerateSliderLabels, useGlobalState } from "senf-shared";
import { capitalizeFirstLetter, getTextWidth } from "senf-shared/utils/helpers";
import { tv, type VariantProps } from "tailwind-variants";
import { v4 as uuidv4 } from "uuid";
import { useOnKeyDown } from "../../../../hooks/useOnKeyDown";
import { cn } from "../../../../util/classNameConstructor";
import RangeSlider from "../../../atoms/rangeSlider/RangeSlider";
import Button, { type ButtonProps } from "../../../core/button/Button";
import Icon from "../../../core/icon/Icon";
import type { IconName } from "../../../core/icon/names";
import InvisibleInput from "../../../core/inputs/InvisibleInput";
import type {
  Question,
  QuestionOption,
  QuestionOptionsKey,
  QuestionValue,
  ValueTypes,
} from "../../../special/survey/survey.types";
import { AddButton } from "../components/AddRowButton";
import { useChoiceState } from "../components/ChoiceItemWrapper";
import EditableContent from "../components/EditableContent";
import { HandleItem } from "../components/HandleItem";
import { pageTypeConfig } from "../pageTypes";
import { maxItems, toggle, type useEditQuestion } from "../utils";
import Textarea from "./Textarea";

const SelectStyles = tv({
  slots: {
    Outer: "grid w-full gap-5",
    Wrapper: "flex w-full flex-wrap gap-1",
    RatingContainer: "flex gap-1",
    ItemWrapper:
      "w-full basis-[var(--item-width)] rounded-lg text-center duration-150 transition-[box-shadow,padding] ring-0 has-[.peer:focus-visible]:ring-2 ring-black has-[.peer:focus-visible]:ring-offset-2 ring-opacity-50 [--tw-ring-inset:unset]",
    Item: "font-bold box-content",
    Star: " cursor-pointer peer/item text-surface-depressed no-touch:peer-hover/item:!text-surface-depressed -mr-1 pr-1 focus:outline-none no-touch:group-hover/items:!text-[var(--color)] [&:focus-visible>svg]:stroke-1",
  },
  variants: {
    stars: {
      true: {
        RatingContainer: "group/items",
      },
      false: {
        RatingContainer: "w-full flex-wrap justify-center",
      },
    },
    monochrome: {
      true: {
        ItemWrapper: "bg-action-secondary-default",
      },
      false: {
        ItemWrapper:
          "bg-[var(--color)] ring-[var(--color)] hover:z-40 hover:bg-[var(--color)] hover:ring-2 hover:ring-[var(--color)] hover:!ring-offset-2 active:bg-[var(--color)] active:ring-[var(--color)]",
      },
    },
    editable: {
      true: {
        Item: "transition-property-[padding] !placeholder-black !placeholder-opacity-40 focus:!placeholder-opacity-20",
      },
      false: {
        ItemWrapper: "cursor-pointer flex items-center justify-center",
        Item: "h-10",
      },
    },
    hasChecked: {
      true: {
        RatingContainer: "checked group",
        Star: "text-surface-depressed peer-[.checked]:text-surface-depressed peer group-[.checked]:text-[var(--color)]",
      },
    },
    checked: {
      true: {
        // Star: "peer/item:text-action-primary-default",
        ItemWrapper: "!ring-2 !ring-black",
        Star: "checked",
      },
    },
  },
  compoundVariants: [
    {
      editable: false,
      monochrome: true,
      class: {
        ItemWrapper: "no-touch:hover:bg-action-secondary-hovered",
      },
    },
  ],
});

interface RatingProps
  extends Question,
    VariantProps<typeof SelectStyles>,
    ReturnType<typeof useEditQuestion> {
  // general props
  mode?: "edit" | "display";
  // display props
  value: ValueTypes;
  onChange?: (props: Partial<QuestionValue>) => void;
}

type HSLA = {
  h: number;
  s: number;
  l: number;
  a: number;
};
const interpolate = (val1: number, val2: number, factor: number) => {
  return Math.round((val1 + (val2 - val1) * factor) * 100) / 100;
};

const interpolateColor = (color1: HSLA, color2: HSLA, factor: number) => {
  const result = {
    h: interpolate(color1.h, color2.h, factor),
    s: interpolate(color1.s, color2.s, factor),
    l: interpolate(color1.l, color2.l, factor),
    a: interpolate(color1.a, color2.a, factor),
  };
  return `hsla(${result.h}, ${result.s}%, ${result.l}%, ${result.a})`;
};

export const getColors = (
  minColor: HSLA,
  centerColor: HSLA,
  maxColor: HSLA,
  steps: number,
) => {
  const colors: string[] = [];
  const stepFactor = 1 / (steps - 1);

  for (let i = 0; i < steps; i++) {
    const color =
      i < steps / 2
        ? interpolateColor(minColor, centerColor, i * stepFactor * 2)
        : interpolateColor(
            steps < 3 ? maxColor : centerColor,
            maxColor,
            (i - steps / 2) * stepFactor * 2,
          );
    colors.push(color);
  }

  return colors;
};

export const minColor = (monochrome?: boolean): HSLA =>
  monochrome ? { h: 44, s: 100, l: 90, a: 1 } : { h: 7, s: 80, l: 72, a: 1 };
export const centerColor = (monochrome?: boolean): HSLA =>
  monochrome ? { h: 43, s: 85, l: 70, a: 1 } : { h: 49, s: 82, l: 75, a: 1 };
export const maxColor = (monochrome?: boolean): HSLA =>
  monochrome ? { h: 41, s: 68, l: 55, a: 1 } : { h: 165, s: 49, l: 68, a: 1 };

const useBalanceWidths = ({ mode }) => {
  const ref = useRef<HTMLDivElement>(null);
  const { t } = useTranslation();
  const children = ref.current?.children || [];
  const n = children.length || 1;
  const width = [...children].reduce?.((acc, button) => {
    const text =
      button.querySelector("textarea")?.value || `${t("global:option")} 00`;
    return Math.max(
      acc,
      getTextWidth(text, "14.5px Nunito") + 40 + (mode === "edit" ? 12 : 0),
    ); // add 12px for the delete button
  }, 0);
  const containerWidth = ref.current?.offsetWidth || 0;

  const possibleColumns = useMemo(
    () => Array.from({ length: 3 }, (_, i) => Math.ceil(n / (i + 1))),
    [n],
  );
  const maxWidth = useMemo(() => {
    const columns =
      possibleColumns.find((col) => containerWidth >= col * width) || 1;
    const gap = 4 - 4 / columns;
    return `calc(${100 / columns}% - ${gap}px)`;
  }, [possibleColumns, containerWidth, width]);

  const wrappingRows = containerWidth < n * width;

  return { maxWidth, wrappingRows, ref };
};

const SimpleSlider = ({
  page,
  option,
  value,
  comment,
  onChange,
  minLabel,
  maxLabel,
  ariaLabeledby,
  ariaDescribedby,
}: {
  page: Question;
  option: QuestionOption;
  value: ValueTypes;
  onChange: (props: Partial<QuestionValue>) => void;
  comment: string;
  minLabel: string;
  maxLabel: string;
  ariaLabeledby?: string;
  ariaDescribedby?: string;
}) => {
  const generateSliderLabels = useGenerateSliderLabels();

  // const min = Math.ceil((page.columns?.length || 0) / -2);
  const min = 1;

  const indexToSliderValue = () => {
    const initialSilderValue = page.columns
      ?.map((e) => e.uid)
      .indexOf(value?.[option.uid]);
    return initialSilderValue === -1
      ? undefined
      : [Math.max(initialSilderValue as number, 0) + min];
  };

  const [sliderValue, setSliderValue] = useState(indexToSliderValue());

  const discreteSlider = (page.columns?.length || 0) <= 10;
  const labels = useMemo(() => {
    if (!discreteSlider) return;
    return generateSliderLabels({
      columns: page.columns || [],
      left: minLabel || "",
      right: maxLabel || "",
      numeric: page.numericSliderLabels,
      min,
    });
  }, [minLabel, maxLabel, page.numericSliderLabels, page.columns, min]);

  // Update slider value when checked value changes
  useEffect(() => {
    setSliderValue(indexToSliderValue());
  }, [value, option.uid, page.columns]);

  const handleChange = useCallback(
    ([val]) => {
      const index = val - min;
      setSliderValue([val]);
      if (page.columns?.[index]) {
        onChange?.({
          value: {
            ...(value || {}),
            [option.uid]: page.columns[index].uid,
          },
          comment: comment || "",
        });
      }
    },
    [onChange, option.uid, page.columns],
  );

  return (
    <RangeSlider
      discrete={discreteSlider}
      value={sliderValue}
      onValueChange={handleChange}
      min={min}
      max={(page.columns?.length || 3) - 1 + min}
      labels={labels}
      ariaLabeledby={ariaLabeledby}
      ariaDescribedby={ariaDescribedby}
    />
  );
};

const SimpleItem = ({
  page,
  option,
  column,
  value,
  comment,
  onChange,
  colIndex,
  mode,
}: {
  page: Question;
  option: QuestionOption;
  column: QuestionOption;
  value: ValueTypes;
  comment: string;
  onChange: (props: Partial<QuestionValue>) => void;
  colIndex: number;
  wrappingRows: boolean;
  mode: "edit" | "display";
}) => {
  const [translation] = useGlobalState().translationsState;
  const { t } = useTranslation();
  const { checked, type } = useChoiceState(page, value, option, column);
  const { Item, ItemWrapper, Star } = SelectStyles({
    monochrome: !!page.monochrome,
    editable: false,
    checked,
    hasChecked: !!value?.[option.uid],
  });

  const iconMap = {
    star: {
      name: "star-fill",
      color: "var(--palette-yellow-500)",
    },
    heart: {
      name: "heart-fill",
      color: "var(--palette-red-800)",
    },
  } satisfies Record<
    string,
    {
      name: IconName;
      color: `var(--palette-${string}-${number})` | `var(--palette-${string})`;
    }
  >;
  const icon = iconMap.star;

  const handleChange = useCallback(() => {
    if (mode === "edit") return;
    onChange?.({
      value: {
        ...(value || {}),
        [option.uid]: toggle(value?.[option.uid], column.uid, type),
      },
      comment: comment || "",
    });
  }, [value, column.uid, onChange, option.uid, type, mode]);

  const colors = getColors(
    minColor(page.monochrome),
    centerColor(page.monochrome),
    maxColor(page.monochrome),
    page.columns?.length || 0,
  );

  const labelProps = (
    page.qtype === "stars"
      ? {
          className: Star(),
          style: {
            "--color": icon.color,
          },
        }
      : {
          className: cn(ItemWrapper(), Item()),
          style: {
            "--color": colors[colIndex],
          },
        }
  ) as React.HTMLAttributes<HTMLLabelElement>;

  /**
   * Handles keyboard navigation for the rating scale.
   * @param e - The keyboard event object.
   * @description Since it is more intuitive to go up in value when pressing the up arrow key, and vice versa, we need to handle the keyboard navigation manually.
   */
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLLabelElement>) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        const previousElement = e.currentTarget.previousElementSibling;
        (previousElement as HTMLElement)?.focus();
        (previousElement as HTMLElement)?.click();
      }
      if (e.key === "ArrowUp") {
        e.preventDefault();
        const nextElement = e.currentTarget.nextElementSibling;
        (nextElement as HTMLElement)?.focus();
        (nextElement as HTMLElement)?.click();
      }
    },
    [],
  );

  return (
    <label
      htmlFor={`${column.uid}-${option.uid}`}
      {...labelProps}
      onKeyDown={handleKeyDown}
    >
      <input
        type="radio"
        id={`${column.uid}-${option.uid}`}
        name={option.uid}
        value={column.value}
        onChange={handleChange}
        checked={checked}
        className="peer sr-only"
      />
      {page.qtype === "ratingScale" ? (
        <span>
          {translation?.[column?.value] || column.value || colIndex + 1}
        </span>
      ) : (
        <>
          <span className="sr-only">
            {t("global:nStars", { count: colIndex + 1 })}
          </span>
          <Icon
            name={icon.name}
            className="h-8 w-8 peer-focus-visible:stroke-focused-black peer-focus-visible:stroke-[1.25px]"
            role="presentation"
          />
        </>
      )}
    </label>
  );
};

const Item = ({
  page,
  option,
  column,
  colIndex,
  itemsEditable,
  ...props
}: {
  page: Question;
  column: QuestionOption;
  option: QuestionOption;
  checked: RatingProps["checked"];
  colIndex: number;
  itemsEditable?: RatingProps["mode"];
} & RatingProps) => {
  const [active, setActive] = useState(false);
  const n = props.columns?.length || 0;
  const deletable =
    n > (pageTypeConfig[props.qtype]?.defaults?.minColumns || 0) &&
    itemsEditable === "edit";

  const { checked, type } = useChoiceState(page, props.value, option, column);
  const { Item, ItemWrapper } = SelectStyles({
    monochrome: !!props.monochrome,
    editable: itemsEditable === "edit",
    checked: checked || active,
  });
  const { t } = useTranslation();
  const colors = getColors(
    minColor(props.monochrome),
    centerColor(props.monochrome),
    maxColor(props.monochrome),
    n,
  );

  return (
    <div
      className={cn("relative flex items-center w-[var(--item-width)]")}
      style={{
        "--color": colors[colIndex],
      }}
    >
      <HandleItem
        active={active}
        type="option"
        deletable={deletable}
        handleDelete={() => props.removeItem(column.uid, "columns")}
        {...props}
        className={cn(
          "absolute z-50 start-0",
          !active && "pointer-events-none",
          !props.monochrome ? "text-black" : "",
        )}
      />
      <EditableContent
        id={column.uid}
        className="text-center"
        value={column?.value}
        onChange={({ target }) => {
          const value = props.columns || [];
          value[colIndex].value = target.value;
          props.updateText(value, option.uid, "columns", undefined, "value");
        }}
        maxRows={1}
        placeholder={`${t("global:option")} ${colIndex + 1}`}
        onClick={(e) => {
          e.stopPropagation();
        }}
        classNames={{
          container: "min-w-0",
          inputContainer: cn(ItemWrapper(), "m-0"),
          input: cn(Item(), "min-w-full"),
        }}
        onFocus={() => setActive(true)}
        onBlur={() => setActive(false)}
      />
    </div>
  );
};

const RatingRow = ({
  itemsEditable,
  removeItem,
  option,
  rowTitle,
  ...props
}: RatingProps) => {
  const { t } = useTranslation();
  const [translation] = useGlobalState().translationsState;

  const {
    maxWidth,
    wrappingRows,
    ref: ratingContainer,
  } = useBalanceWidths({
    mode: props.mode,
  });

  const generateSliderLabels = useGenerateSliderLabels();
  const { Wrapper, RatingContainer } = SelectStyles({
    stars: props.qtype === "stars",
    hasChecked:
      !!props.value && !!Object.values(props.value).filter(Boolean).length,
  });
  const value = props.columns
    ?.map((e) => e.uid)
    .indexOf(props.value?.[option.uid]);

  const [sliderValue, setSliderValue] = useState(
    value === -1 ? undefined : [Math.max(value as number, 0)],
  );

  // Update slider value when checked value changes
  useEffect(() => {
    const newValue = props.columns
      ?.map((e) => e.uid)
      .indexOf(props.value?.[option.uid]);
    setSliderValue(
      newValue === -1 ? undefined : [Math.max(newValue as number, 0)],
    );
  }, [props.value, option.uid, props.columns]);

  const discreteSlider = (props.columns?.length || 0) <= 10;
  const subTitle = translation?.[rowTitle?.value] || rowTitle?.value;

  const minLabel =
    translation?.[props.minLabel?.value] || props.minLabel?.value || "";

  const maxLabel =
    translation?.[props.maxLabel?.value] || props.maxLabel?.value || "";

  const legendId = subTitle
    ? `sub-question-legend-${option.uid}`
    : "question-legend";

  const descriptionId = subTitle
    ? `sub-question-description-${option.uid}`
    : "question-description";

  const firstColumn = props.columns?.[0].value;
  const lastColumn = props.columns?.[props.columns.length - 1].value;

  if (props.mode === "display") {
    return (
      <fieldset
        className={Wrapper({ className: "p-0" })}
        aria-labelledby={!subTitle ? legendId : undefined}
        aria-describedby={descriptionId}
      >
        {subTitle && (
          <legend
            className="body-md-bold py-2 w-full -order-2 mb-1"
            id={legendId}
          >
            {subTitle}
          </legend>
        )}

        {props.qtype === "slider" && (
          <div id={descriptionId} className="sr-only">
            {t("survey:accessibility.slider.description", {
              min: minLabel,
              max: maxLabel,
            })}
          </div>
        )}
        {props.qtype === "ratingScale" && (
          <div id={descriptionId} className="sr-only">
            {t("survey:accessibility.ratingScale.description", {
              min: `${minLabel} ${firstColumn ? `(${firstColumn})` : ""}`,
              max: `${maxLabel} ${lastColumn ? `(${lastColumn})` : ""}`,
            })}
          </div>
        )}
        <div
          className={RatingContainer()}
          ref={ratingContainer}
          style={{ "--item-width": maxWidth }}
          key={`rating-${props.columns?.length}`}
        >
          {props.qtype === "slider" ? (
            <SimpleSlider
              {...props}
              option={option}
              minLabel={minLabel}
              maxLabel={maxLabel}
              ariaLabeledby={legendId}
              ariaDescribedby={descriptionId}
            />
          ) : (
            props.columns.map((column, i) => (
              <SimpleItem
                {...props}
                key={column.uid}
                column={column}
                wrappingRows={wrappingRows}
                colIndex={i}
                option={option}
              />
            ))
          )}
        </div>
        {props.qtype !== "stars" && (
          <>
            {minLabel && (
              <span
                className={cn(
                  "body-sm inline-block py-2",
                  wrappingRows ? "-order-1" : "order-1 w-5/12",
                )}
                role="presentation"
              >
                {minLabel}
              </span>
            )}
            {maxLabel && (
              <span
                className={cn(
                  "body-sm inline-block text-end ml-auto py-2",
                  wrappingRows ? "" : "order-2 w-5/12",
                )}
                role="presentation"
              >
                {maxLabel}
              </span>
            )}
          </>
        )}
      </fieldset>
    );
  }
  return (
    <div className={Wrapper()}>
      <EditableContent
        className="body-md-bold"
        value={translation?.[rowTitle?.value] || rowTitle?.value}
        onChange={({ target }) => {
          props.updateText(
            {
              uid: rowTitle?.uid || uuidv4(),
              value:
                target.value || target.value === ""
                  ? target.value
                  : rowTitle.value,
            },
            option.uid,
            "options",
            undefined,
            "rowTitle",
          );
        }}
        placeholder={`${t("global:question")}`}
        classNames={{
          container: "-order-1",
        }}
        mode={props.mode}
      />
      <div
        className={RatingContainer()}
        ref={ratingContainer}
        style={{ "--item-width": maxWidth }}
        key={`slider-${props.columns?.length}`}
      >
        {props.qtype === "slider" ? (
          <RangeSlider
            discrete={discreteSlider}
            value={sliderValue}
            onValueChange={([value]) => setSliderValue([value])}
            onValueCommit={() => setSliderValue(undefined)}
            min={1}
            max={props.columns?.length || 3}
            labels={
              discreteSlider
                ? generateSliderLabels({
                    columns: props.columns || [],
                    left: minLabel,
                    right: maxLabel,
                    numeric: props.numericSliderLabels,
                  })
                : undefined
            }
          />
        ) : null}
        {props.columns?.map((column, i) =>
          props.qtype === "stars" ? (
            <SimpleItem
              key={column.uid}
              value={value}
              onChange={props.onChange}
              column={column}
              option={option}
              {...props}
            />
          ) : props.qtype === "ratingScale" ? (
            <Item
              key={column.uid}
              column={column}
              value={value}
              onChange={props.onChange}
              option={option}
              colIndex={i}
              itemsEditable={itemsEditable}
              {...props}
              wrappingRows={wrappingRows}
              removeItem={removeItem}
            />
          ) : null,
        )}
      </div>
      {props.qtype !== "stars" && (
        <>
          <EditableContent
            className="body-sm"
            size="md"
            required={props.qtype === "slider"}
            value={minLabel}
            onChange={({ target }) => {
              props.updateText(
                [
                  {
                    uid: props.minLabel?.uid || uuidv4(),
                    value:
                      target.value || target.value === ""
                        ? target.value
                        : props.minLabel?.value,
                  },
                  {
                    uid: props.maxLabel?.uid || uuidv4(),
                    value: props.maxLabel?.value,
                  },
                ],
                option.uid,
                "options",
                undefined,
                "labels",
              );
            }}
            placeholder={`${t("survey:builder.descriptionForMinimumValue")}`}
            classNames={{
              container: cn(wrappingRows ? "-order-1" : "order-1 w-5/12"),
              inputContainer: itemsEditable === "edit" ? "my-0.5" : "",
            }}
            mode={itemsEditable || "display"}
          />
          <EditableContent
            className="body-sm"
            size="md"
            required={props.qtype === "slider"}
            value={maxLabel}
            onChange={({ target }) =>
              props.updateText(
                [
                  {
                    uid: props.minLabel?.uid || uuidv4(),
                    value: props.minLabel?.value,
                  },
                  {
                    uid: props.maxLabel?.uid || uuidv4(),
                    value:
                      target.value || target.value === ""
                        ? target.value
                        : props.maxLabel?.value,
                  },
                ],
                option.uid,
                "options",
                undefined,
                "labels",
              )
            }
            placeholder={`${t("survey:builder.descriptionForMaximumValue")}`}
            classNames={{
              container: cn(wrappingRows ? "" : "order-2 ml-auto w-5/12"),
              inputContainer: itemsEditable === "edit" ? "my-0.5" : "",
              input: "text-right",
            }}
            mode={itemsEditable || "display"}
          />
        </>
      )}
    </div>
  );
};

const NewRating = ({ onChange, ...props }: RatingProps) => {
  const { Outer } = SelectStyles({
    stars: props.qtype === "stars",
    hasChecked:
      !!props.value && !!Object.values(props.value).filter(Boolean).length,
  });
  const { t } = useTranslation();

  const addItem = (key: QuestionOptionsKey) => {
    props.addItem(key, { focus: true, maxItems: key === "columns" });
  };
  const removeItem = (uid: string, key: QuestionOptionsKey) => {
    props.removeItem(uid, key, { focus: true });
  };

  const itemsEditable =
    props.qtype === "ratingScale" || props.qtype === "slider"
      ? props.mode
      : undefined;

  return (
    <div className={Outer()}>
      {props.mode === "edit" && props.qtype !== "slider" && (
        <AddButton
          text={t("global:option")}
          onClick={() => addItem("columns")}
          show={itemsEditable === "edit"}
          id="pageColumns-addButton"
          disabled={!!maxItems(props, "columns", props.qtype)}
          disabledNotice={`${t("survey:options.youMustAddMaxOptions", {
            count: maxItems(props, "columns", props.qtype),
            choiceType: t(
              maxItems(props, "columns", props.qtype) === 1
                ? "global:option"
                : "global:options",
            ),
          })}`}
          className="ml-auto pb-2"
        />
      )}
      {props.options?.map((option) => {
        const { uid, labels, rowTitle } = option;
        const [minLabel, maxLabel] = labels || [
          { uid: "", value: "" },
          { uid: "", value: "" },
        ];

        return (
          <RatingRow
            key={uid}
            {...props}
            itemsEditable={itemsEditable}
            removeItem={removeItem}
            minLabel={minLabel}
            maxLabel={maxLabel}
            option={option}
            rowTitle={rowTitle}
            onChange={onChange}
          />
        );
      })}
      {props.mode === "edit" && (
        <div className="mb-5 flex gap-2">
          <AddButton
            text={t("global:row")}
            onClick={() => addItem("options")}
            show={props.mode === "edit"}
            id="pageColumns-addButton"
          />
          {(props.options?.length || 0) > 1 && (
            <AddButton
              text={t("global:row")}
              leadingIcon="minus-md"
              variant="outline"
              onClick={() =>
                props.options &&
                removeItem(
                  props.options[props.options.length - 1]?.uid,
                  "options",
                )
              }
              show={props.mode === "edit"}
              id="pageColumns-addButton"
              disabledNotice={`${t("survey:options.youMustAddMaxOptions", {
                count: maxItems(props, "options", props.qtype),
                choiceType: t(
                  maxItems(props, "options", props.qtype) === 1
                    ? "global:option"
                    : "global:options",
                ),
              })}`}
            />
          )}
        </div>
      )}
      {props.textResponse && (
        <Textarea
          {...props}
          value={props.comment}
          onChange={(comment) => {
            onChange?.({ value: props.value, comment });
          }}
          textRequired={props.textResponseRequired}
          textTitle={
            props.textTitle === undefined
              ? props.defaultTextTitle
              : props.textTitle
          }
          placeholder={props.defaultTextTitle}
        />
      )}
    </div>
  );
};
NewRating.displayName = "Rating";
export default NewRating;
